import { Mesh, type Color, type Material, type Node, type Object3D } from 'three/webgpu';
import { add, smoothstep, step, uniform, vec3 } from 'three/tsl';

import { COLORS } from '../core/constants';
import { LATTICE_DEFAULTS, hexField, screenOrigin } from './burn';

/** Ripples that can be in flight at once; a fresh one takes the oldest slot. */
const SLOTS = 3;
/** Width of the lit band trailing the front, in the field's own units. */
const BAND = 0.1;
/** Where along its run a ripple starts to die away. */
const FADE_FROM = 0.45;

export interface PulseOptions {
  /** Hex rows down the height of the screen, at the origin. Higher is tighter. */
  density?: number;
  /** How many times wider a cell at the far corner is than one at the origin. */
  growth?: number;
  /** `0` is a clean ring, `1` lets the noise decide the whole order. */
  turbulence?: number;
  /** How much of a cell lights at once: low flashes it whole, high lights a ring inside it. */
  softness?: number;
  /** How long one ripple takes to cross the screen. */
  seconds?: number;
  colour?: Color;
  /** How far above white the band is driven. */
  gain?: number;
  seed?: number;
}

export interface Pulse {
  /** Starts a ripple out of a point on the screen, in screen UV with y down. */
  trigger(origin: { x: number; y: number }): void;
  /** Advances every ripple in flight. Call once a frame. */
  update(delta: number): void;
  /** Adds the ripples to the emissive of every material under `root`. */
  applyTo(root: Object3D): void;
  /** Puts every material this pulse touched back the way it was. */
  dispose(): void;
}

/** How far along its run a ripple is: 0 leaving the origin, 1 gone. */
function progressUniform() {
  return uniform(1);
}

interface Slot {
  origin: ReturnType<typeof screenOrigin>;
  progress: ReturnType<typeof progressUniform>;
}

interface Emissive {
  emissiveNode?: Node<'vec3'> | null;
  needsUpdate: boolean;
}

/**
 * One slot's contribution: a band of lit cells just behind a front that sweeps
 * out from the slot's origin, dying away as it goes.
 */
function ripple(slot: Slot, options: Required<PulseOptions>, seed: number) {
  const { field } = hexField(slot.origin, {
    ...LATTICE_DEFAULTS,
    density: options.density,
    growth: options.growth,
    turbulence: options.turbulence,
    softness: options.softness,
    radial: true,
    seed,
  });

  // Swept far enough past the end that a finished ripple has left the screen.
  const front = slot.progress.mul(1 + options.softness + BAND);
  const band = smoothstep(front.sub(BAND), front, field).mul(step(field, front));
  const fade = smoothstep(FADE_FROM, 1, slot.progress).oneMinus();
  return band.mul(fade);
}

/**
 * A radial ripple over the instrument: the same hex lattice as the burn, but
 * instead of cutting cells away it lights a ring of them and sends it out from
 * a point on the screen. Several can run at once.
 */
export function createPulse(options: PulseOptions = {}): Pulse {
  const settings: Required<PulseOptions> = {
    density: 480,
    growth: 1.6,
    turbulence: 0.1,
    softness: 0.12,
    seconds: 1.1,
    colour: COLORS.teal,
    gain: 1.6,
    seed: 0,
    ...options,
  };

  // Every slot starts finished, so nothing shows until a trigger.
  const slots: Slot[] = Array.from({ length: SLOTS }, () => ({
    origin: screenOrigin(0.5, 0.5),
    progress: progressUniform(),
  }));

  const ripples = slots.map((slot, index) => ripple(slot, settings, settings.seed + index));
  const lit = ripples.reduce((sum, part) => add(sum, part));
  const { colour, gain } = settings;
  const emissive = vec3(colour.r, colour.g, colour.b).mul(gain).mul(lit);

  const touched: { material: Emissive; emissiveNode: Node<'vec3'> | null }[] = [];

  return {
    trigger(origin) {
      let slot = slots[0];
      for (const candidate of slots) {
        if (candidate.progress.value > slot.progress.value) slot = candidate;
      }
      slot.origin.value.set(origin.x, origin.y);
      slot.progress.value = 0;
    },

    update(delta) {
      for (const slot of slots) {
        slot.progress.value = Math.min(1, slot.progress.value + delta / settings.seconds);
      }
    },

    applyTo(root) {
      const materials = new Set<Material>();
      root.traverse((child) => {
        if (child instanceof Mesh) materials.add(child.material as Material);
      });

      for (const material of materials) {
        const target = material as unknown as Emissive;
        const existing = target.emissiveNode ?? null;
        touched.push({ material: target, emissiveNode: existing });
        // On top of whatever is already there, which is the burn's rim.
        target.emissiveNode = existing ? add(existing, emissive) : emissive;
        target.needsUpdate = true;
      }
    },

    dispose() {
      for (const entry of touched) {
        entry.material.emissiveNode = entry.emissiveNode;
        entry.material.needsUpdate = true;
      }
      touched.length = 0;
    },
  };
}
